import { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ContentWrapper from '../../components/ContentWrapper';
import { ApiRoutes, AppRoutes } from '../../constants/routes';
import { mutation } from '../../utils/api';
import { useAuthProvider } from '../../utils/auth';

// Every account is a person. Accounts that signed up as a label or
// publisher had no name of their own (the company's was used); this asks
// for it once. The company keeps its name, in the switcher at the top.
const AboutYou = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser, dashboardRoute, workingAs } = useAuthProvider();
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const companies = [...(user?.organizations?.labels || []), ...(user?.organizations?.publishers || [])]
    .filter((org) => org.role === 'Owner');

  const save = async (event) => {
    event.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('Enter your first and last name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await mutation(ApiRoutes.ProfileUpdate, { first_name: firstName.trim(), last_name: lastName.trim() }, 'PUT');
      await refreshUser();
      navigate(location.state?.from || (workingAs ? dashboardRoute : AppRoutes.Home2), { replace: true });
    } catch (e) {
      setError(e.message || "Couldn't save. Check your connection and try again.");
      setSaving(false);
    }
  };

  return (
    <ContentWrapper>
      <Box sx={{ maxWidth: 560, mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>About you</Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          Your account is yours as a person: your own listening, credit, playlists and followers.
        </Typography>
        {companies.length > 0 && (
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {companies.map((org) => org.name).join(' and ')} {companies.length === 1 ? 'keeps its' : 'keep their'} own
            name and profile. Switch to {companies.length === 1 ? 'it' : 'them'} from the menu at the top of the page.
          </Typography>
        )}
        <Typography color="text.secondary" sx={{ mb: 3 }}>What's your name?</Typography>
        <Paper component="form" onSubmit={save} noValidate elevation={1}
               sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField id="about-first-name" label="First name" value={firstName} autoFocus required
                       onChange={(e) => setFirstName(e.target.value)} sx={{ flex: '1 1 200px' }} />
            <TextField id="about-last-name" label="Last name" value={lastName} required
                       onChange={(e) => setLastName(e.target.value)} sx={{ flex: '1 1 200px' }} />
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving…' : 'Continue'}</Button>
          </Box>
        </Paper>
      </Box>
    </ContentWrapper>
  );
};

// Sends anyone signed in without a name of their own to About you, except
// while they're signing up, signing in or already there
const EXEMPT = ['/about-you', '/login', '/signup', '/account-create', '/verify', '/reset', '/forgot', '/logout'];

export const PersonalDetailsGate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed, isLoading, user } = useAuthProvider();
  const missing = isAuthed && !isLoading && user && user.signup_completed !== false && !user.first_name;
  const exempt = EXEMPT.some((path) => location.pathname.startsWith(path));
  useEffect(() => {
    if (missing && !exempt) navigate('/about-you', { replace: true, state: { from: location.pathname } });
  }, [missing, exempt, location.pathname, navigate]);
  return null;
};

export default AboutYou;
